<template>
  <div class="home-container">
    <h2>Welcome to NPCode App</h2>
    <p class="mb-4">This dashboard contains links to several functions.</p>

    <!-- Title for Quick Links -->
    <v-subheader class="mt-4 mb-2">Quick Links</v-subheader>

    <!-- Divider below the title -->
    <v-divider class="mb-4"></v-divider>

    <!-- Card Grid for Functions -->
    <v-slide-group show-arrows>
      <!-- Card for Remove Comas function -->
      <v-slide-item class="ma-2">
        <v-card
          class="function-card"
          @click="$router.push('/remove-comas')"
          outlined
        >
          <v-img
            src="https://cdn-icons-png.flaticon.com/512/565/565239.png"
            contain
            max-height="150"
          ></v-img>
          <v-card-title>Remove Comas</v-card-title>
          <v-card-subtitle class="text-subtitle">
            Remove commas from text strings.
          </v-card-subtitle>
        </v-card>
      </v-slide-item>

      <!-- Card for Add Comas function -->
      <v-slide-item class="ma-2">
        <v-card
          class="function-card"
          @click="$router.push('/add-comas')"
          outlined
        >
          <v-img
            src="https://cdn-icons-png.flaticon.com/512/1828/1828884.png"
            contain
            max-height="150"
          ></v-img>
          <v-card-title>Add Comas</v-card-title>
          <v-card-subtitle class="text-subtitle">
            Add commas after every two digits.
          </v-card-subtitle>
        </v-card>
      </v-slide-item>

      <!-- Card for SGDE to AED converter -->
      <v-slide-item class="ma-2">
        <v-card
          class="function-card"
          @click="$router.push('/sgde-to-aed')"
          outlined
        >
          <v-img
            src="https://cdn-icons-png.flaticon.com/512/1995/1995470.png"
            contain
            max-height="150"
          ></v-img>
          <v-card-title>SGDE -> AED</v-card-title>
          <v-card-subtitle class="text-subtitle">
            Convert SGDE dumps into AED files.
          </v-card-subtitle>
        </v-card>
      </v-slide-item>

      <!-- Card for VCX to AED converter -->
      <v-slide-item class="ma-2">
        <v-card
          class="function-card"
          @click="$router.push('/vcx-to-aed')"
          outlined
        >
          <v-img
            src="https://cdn-icons-png.flaticon.com/512/10342/10342550.png"
            contain
            max-height="150"
          ></v-img>
          <v-card-title>VCX -> AED</v-card-title>
          <v-card-subtitle class="text-subtitle">
            Split VCX exports into module AED files.
          </v-card-subtitle>
        </v-card>
      </v-slide-item>
    </v-slide-group>

    <v-subheader class="mt-8 mb-2">Professional Services</v-subheader>
    <v-row dense>
      <v-col
        v-for="service in services"
        :key="service.title"
        cols="12"
        md="6"
        lg="3"
      >
        <v-card class="service-card" outlined>
          <v-card-title class="service-title">
            <v-icon class="mr-2" size="28" color="primary">
              {{ service.icon }}
            </v-icon>
            <span>{{ service.title }}</span>
          </v-card-title>
          <v-card-text>{{ service.description }}</v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              prepend-icon="mdi-whatsapp"
              @click="contactService(service)"
            >
              Contact
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="mt-8 npshops-card" outlined>
      <v-card-title>
        <v-icon class="mr-2" color="primary">mdi-storefront</v-icon>
        NPShops.com Ecosystem
      </v-card-title>
      <v-card-subtitle>
        Official NazirProg tools & services store for Mercedes retrofit experts.
      </v-card-subtitle>
      <v-card-text>
        <p>
          NPShops.com represents NazirProg in delivering calibrated tools,
          retrofit hardware, and digital service packs to workshops worldwide.
          Discover:
        </p>
        <ul>
          <li>
            Genuine diagnostic interfaces, retrofit harnesses, and flashing
            kits—bench tested by NazirProg.
          </li>
          <li>
            Ready-made coding bundles, dataset packs, and SecOC unlock services
            for new Mercedes platforms.
          </li>
          <li>
            On-demand engineering support for hardware coding, flashing, and
            retrofit validation.
          </li>
        </ul>
        <p>
          Ready to upgrade your toolkit? Visit
          <a href="https://www.npshops.com" target="_blank" rel="noopener">
            NPShops.com
          </a>
          or chat directly with the NazirProg/NPShops team via WhatsApp.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="secondary"
          href="https://www.npshops.com"
          target="_blank"
          rel="noopener"
        >
          Visit NPShops.com
        </v-btn>
        <v-btn
          color="primary"
          prepend-icon="mdi-whatsapp"
          @click="contactNpshops"
        >
          WhatsApp NPShops
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "HomePage",
  data() {
    return {
      services: [
        {
          icon: "mdi-remote-desktop",
          title: "Remote Programming Solutions",
          description: "Remote programming solutions for Mercedes vehicles.",
          message:
            "Hello! I'm interested in your remote programming solutions for Mercedes.",
        },
        {
          icon: "mdi-map-marker-path",
          title: "Map Data & Activation Codes",
          description:
            "Map data and activation codes for NTG3.5/4.5/5/5.5/6/7 systems.",
          message:
            "Hi! I need map data and activation codes for NTG systems (3.5/4.5/5/5.5/6/7).",
        },
        {
          icon: "mdi-lock-check",
          title: "Antitheft Code",
          description:
            "Fast retrieval of radio and head-unit antitheft security codes.",
          message: "Hello! I would like to request an antitheft code service.",
        },
        {
          icon: "mdi-shield-lock",
          title: "Complete Coding & SecOC",
          description:
            "Full coding & SecOC (w223, w206, w214, w213, w254, w167, w118, w295, etc.) solutions for new-generation Mercedes coders.",
          message:
            "Hi! I'm interested in SecOC coding solutions for the latest Mercedes platforms (W223/W206/W214/W254/etc.).",
        },
      ],
    };
  },
  methods: {
    contactService(service) {
      const text = encodeURIComponent(
        `${service.title} Inquiry\n\n${service.message}`
      );
      window.open(`https://wa.me/96176469052?text=${text}`, "_blank");
    },
    contactNpshops() {
      const message =
        "Hello NPShops.com / NazirProg! I'm interested in your tools, retrofit hardware, and SecOC service packs.";
      window.open(
        `https://wa.me/96176469052?text=${encodeURIComponent(message)}`,
        "_blank"
      );
    },
  },
};
</script>

<style>
.home-container {
  padding: 20px;
  text-align: left;
}

.function-card {
  cursor: pointer;
  text-align: center;
  padding: 10px;
  width: 150px;
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease;
  overflow: visible; /* Allow overflow for the shadow */
}

.function-card .v-img {
  height: 40px;
  width: 40px;
}

.function-card:hover {
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
}

.function-card .v-card-title {
  font-size: 1rem;
  margin-top: 0px;
}

.function-card .v-card-subtitle {
  font-size: 0.7rem;
  text-align: left;
  width: 100%;
  padding: 0;
}

/* Add custom styling if needed */
.function-card {
  cursor: pointer;
  transition: transform 0.3s;
}

.function-card:hover {
  transform: scale(1.05);
}

.v-slide-group__content {
  padding: 24px 8px;
}

.v-slide-group__container {
}

.v-slide-group {
}

v-slide-item {
}

.service-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.service-title {
  font-weight: 600;
  align-items: center;
  display: flex;
}

.npshops-card ul {
  padding-left: 20px;
}
</style>
